/**
 * SAP CONFIDENTIAL 
 *
 * This software is confidential proprietary software licensed by SAP SE 
 * and is not to be used, duplicated or disclosed to anyone without the 
 * prior written permission of SAP. 
 *
 * (C) 2020 Copyright SAP SE.
 */
import { Component, OnInit, OnChanges, Input, Output, SimpleChanges, EventEmitter } from '@angular/core';
import { CommonService } from '../services/common.service';
import { DomSanitizer } from '@angular/platform-browser';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import { DS_PRO_HELP_MENU_ID } from '../../config/constants';

@Component({
  selector: 'app-page-help',
  templateUrl: './page-help.component.html',
  styleUrls: ['./page-help.component.scss']
})
export class PageHelpComponent implements OnInit, OnChanges {
  @Input() helpObj:any = {};
  @Output() closeSlider: EventEmitter<any> = new EventEmitter();
  public pageHelp = true;
  public helpApiLoaded = false;
  public helpContent = '';
  public hasHelpUpdateAccess = true;
  public deleteMsg = '';
  public appHelpObj: any = {};
  public helpTranslations: any = {};
  public actionType ='';
  public currentIndex: any = null;

  constructor(
    private commonService: CommonService,
    private toastr: ToastrService,
    public sanitizer: DomSanitizer,
    public translate: TranslateService) {
    this.hasHelpUpdateAccess = this.commonService.hasFeatureAccess('Help Update');
    this.appHelpObj['menuId']= DS_PRO_HELP_MENU_ID.menuId;
    this.deleteMsg = this.getTranslationWithKey('help_content_delete_message');
    this.appHelpObj['header'] = this.getTranslationWithKey('dspro_help');
    this.translate.get('common.help').subscribe(label => {
      this.helpTranslations = label;
      this.appHelpObj['header'] = label.dsproHelp;
    });
  }

  ngOnInit() {
    this.getAppHelpContent();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['helpObj'] && changes['helpObj'].currentValue && changes['helpObj'].currentValue.menuId) {
      this.pageHelp = true;
      this.getHelpContent(changes['helpObj'].currentValue);
    }
  }

  public getTranslationWithKey(key: string): string {
    return this.commonService.getTranslatedVal(key);
  }

  private getAppHelpContent(){ /* For APP Help */
    this.commonService.getHelpContent(this.appHelpObj)
    .then(this.getAppHelpContentSH.bind(this), (reason) =>this.commonService.commonErrorHandler(reason));
  }

  private getAppHelpContentSH(response: any) {}

  private getHelpContent(reqObj: any){
    this.commonService.getHelpContent(reqObj)
    .then(this.getHelpContentSH.bind(this), (reason) =>this.commonService.commonErrorHandler(reason));
  }

  private getHelpContentSH(response: any) {
    let resObj: any = {};
    const data = response ? response.data : '';
    this.helpApiLoaded = true;
    if(data && data.body){
      resObj = data;
      const resSections = JSON.parse(decodeURIComponent(escape(window.atob(data.body))));
      if(this.actionType == 'Edit'){ /* Just replacing the edited section instead of assigning all the response to sections array */
        this.helpObj.sections[this.currentIndex] = JSON.parse(JSON.stringify(resSections[this.currentIndex]));
        this.helpObj.lastSavedData[this.currentIndex] = JSON.parse(JSON.stringify(resSections[this.currentIndex]));
      }else if(!this.actionType){
        this.helpObj.sections = JSON.parse(JSON.stringify(resSections));
        this.helpObj.lastSavedData = JSON.parse(JSON.stringify(this.helpObj.sections));
      }
    }else{
      this.helpObj.sections = [{
        header: this.getTranslationWithKey('app_name')+' '+this.helpObj.header+' '+this.helpTranslations.pageLbl,
        sectionText: '',
        mainSection: true
      }];
      this.helpObj.lastSavedData = JSON.parse(JSON.stringify(this.helpObj.sections));
    }
    this.helpObj['id'] = resObj['id'] ||'';
    this.helpObj['body'] = resObj['body'];
    this.actionType = '';
    this.currentIndex = null;
  }

  public displayHelpPageDetails(pageHelp: any) {
    this.pageHelp = pageHelp;
  }

  public saveHelpContent(actionType: any, currentIndex: any){
    this.actionType = actionType;
    this.currentIndex = currentIndex;
    const requestPayload = this.createRequestPayload();
    this.commonService.saveHelpContent(requestPayload)
    .then(this.saveHelpContentSH.bind(this), (reason) =>this.commonService.commonErrorHandler(reason));
  }

  private createRequestPayload(){
    const requestPayload: any = {};
    if(this.helpObj.id){
      requestPayload['id'] = this.helpObj.id;
    }
    requestPayload['operation'] = this.helpObj.id ? 'Update' : 'Create';
    requestPayload['menuId'] = this.helpObj.menuId;
    const tabCode = this.helpObj.tab;
    if(tabCode){
      requestPayload['tab'] = tabCode;
    }
    const sections = JSON.parse(JSON.stringify(this.helpObj.sections));
    sections.forEach((section: any) => {
      delete section.editable;
      delete section.mainSection;
    });

    if(requestPayload['id'] && !this.helpObj.sections.length){
      requestPayload['operation'] = 'Delete';
      requestPayload['body'] = '';
    }else{
      requestPayload['body'] = window.btoa(unescape(encodeURIComponent(JSON.stringify(sections))));
    }
    return requestPayload;
  }

  private saveHelpContentSH(response: any){
    this.toastr.success(this.getTranslationWithKey('operation_success'), this.getTranslationWithKey('success'));
    this.getHelpContentSH(response);
  }

  public redirectToPage(path: any) {
    const url= window.location.origin + '/#' + path;
    window.open(url, '_blank');
  }

  public closePageSlider() {
    this.clearData();
    this.closeSlider.emit();
  }

  private clearData(){
    this.helpObj = {};
    this.appHelpObj = {};
  }

  public cancelEdit(index: any) {
    const lastSavedObj = this.helpObj.lastSavedData[index];
    if(lastSavedObj){
      this.helpObj.sections[index] = JSON.parse(JSON.stringify(lastSavedObj));
    }else{
      this.helpObj.sections.splice(index, 1);
    }
  }

  public deleteSection(index: any) {
    this.helpObj.sections.splice(index, 1);
    this.helpObj.lastSavedData.splice(index, 1);
    this.saveHelpContent('Delete', index);
  }

  public addSubSection(){
    this.helpObj.sections.push({
      header: '',
      sectionText: '',
      editable:true
    });
  }
}
